import { memo, useCallback, useEffect, useMemo, useRef } from 'react'

import 'pannellum/src/js/libpannellum'
import 'pannellum/src/js/pannellum'
import 'pannellum/src/css/pannellum.css'

import { useKnownPosition } from '../../hooks/useKnownPosition'
import { HOME_SCENE_ID, usePannellumViewer } from '../../hooks/usePannellum'
import { PannellumViewer } from '../../types'
import {
  areViewerPositionsAlmostEqual,
  applyViewerPositionOffset,
  normalizeViewerPosition,
} from '../../utils'
import { ViewerPosition } from '../views/image_viewer/types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper window type
export const pannellum = (window as any).pannellum

interface PannellumProps {
  image: string
  viewerId: string
  viewerPosition: ViewerPosition
  viewerPositionDelta?: ViewerPosition
  isSolo?: boolean
  angleOffset: number
  followRealtime?: boolean
  syncedTo?: string
  onDragging?: (isActive: boolean) => void
  onUpdate?: (newValues: ViewerPosition) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper error type
  onError?: (err: any) => void
  /** Fired when a panorama scene finishes loading (preferred over dual hook `loaded`). */
  onLoad?: () => void
  /** Optional zoom-in floor (degrees). Defaults to Pannellum's 50. */
  minHfov?: number
  /** Optional zoom-out ceiling (degrees). Defaults to Pannellum's 120. */
  maxHfov?: number
  /**
   * When this key changes, force lookAt to the current viewerPosition, bypassing
   * the prop-sync guard that skips lagging React props over a settled live pose.
   * Use for intentional retargets (e.g. Safety/QC aiming at a label).
   */
  poseCommandKey?: string | null
}

export const Pannellum = memo(
  ({
    image,
    viewerId,
    isSolo,
    viewerPosition,
    angleOffset,
    followRealtime = false,
    syncedTo,
    onDragging,
    onUpdate,
    onError,
    onLoad,
    minHfov,
    maxHfov,
    poseCommandKey = null,
  }: PannellumProps) => {
    const ref = useRef<HTMLDivElement>(null)
    const { viewer, createViewer, destroyViewer } = usePannellumViewer(viewerId)
    const isMouseDown = useRef<boolean>(false)
    // Ignore async Image/XHR completions and error events after unmount/destroy.
    // Destroy aborts in-flight loads; pannellum can fire `error` after React has
    // already reset parent imageLoadError for the next observation.
    const mountedRef = useRef(true)
    const onErrorRef = useRef(onError)
    onErrorRef.current = onError
    const onLoadRef = useRef(onLoad)
    onLoadRef.current = onLoad
    const onDraggingRef = useRef(onDragging)
    onDraggingRef.current = onDragging
    const onUpdateRef = useRef(onUpdate)
    onUpdateRef.current = onUpdate
    const followRealtimeRef = useRef(followRealtime)
    followRealtimeRef.current = followRealtime
    // Tracked outside of context state so async image loads see the live viewer.
    const viewerInstanceRef = useRef<PannellumViewer | null>(null)
    const caughtUpViewerRef = useRef<PannellumViewer | null>(null)
    // Pannellum ignores `loadScene` while another scene is mid-load; remember
    // that a swap is owed so it can be applied once the viewer settles.
    const pendingSceneRef = useRef(false)
    // Pannellum's isLoaded() returns false both while loading and after errors,
    // so track the state we control explicitly.
    const sceneLoadingRef = useRef(false)
    // What the viewer is currently showing, so an offset change re-orients the
    // live panorama instead of racing an incoming image.
    const appliedSrcRef = useRef<string | null>(null)
    const appliedOffsetRef = useRef<number | null>(null)
    const dragRafRef = useRef<number | null>(null)
    // applyScene can run from a synchronous Image.onload before the prop-sync
    // effect; keep the latest parent pose available for the first scene.
    const viewerPositionRef = useRef(viewerPosition)
    viewerPositionRef.current = viewerPosition
    const appliedPoseCommandKeyRef = useRef<string | null>(null)

    const injectOffset = useCallback(
      (clean: ViewerPosition) => {
        return applyViewerPositionOffset(clean, -angleOffset)
      },
      [angleOffset],
    )

    const removeOffset = useCallback(
      (tampered: ViewerPosition) => {
        return applyViewerPositionOffset(tampered, angleOffset)
      },
      [angleOffset],
    )

    const handleLookAt = useCallback(
      (positionToLookAt: ViewerPosition) => {
        if (viewer && !isMouseDown.current) {
          const withOffset = injectOffset(positionToLookAt)
          const normalized = normalizeViewerPosition(withOffset)
          viewer.lookAt(
            normalized.pitch,
            normalized.yaw,
            normalized.hfov,
            false,
          )
        }
      },
      [viewer, injectOffset],
    )

    const handleUpdate = useCallback(
      (positionToPropagate: ViewerPosition) => {
        if (!followRealtime) {
          onUpdate && onUpdate(positionToPropagate)
        }
      },
      [followRealtime, onUpdate],
    )

    const { knownPosition, updateKnownPosition } = useKnownPosition(
      handleLookAt,
      handleUpdate,
    )

    // The viewer is created by the image effect below (it needs a panorama);
    // this only owns teardown.
    useEffect(() => {
      mountedRef.current = true
      return () => {
        mountedRef.current = false
        viewerInstanceRef.current = null
        caughtUpViewerRef.current = null
        pendingSceneRef.current = false
        sceneLoadingRef.current = false
        if (dragRafRef.current != null) {
          cancelAnimationFrame(dragRafRef.current)
          dragRafRef.current = null
        }
        destroyViewer()
      }
    }, [destroyViewer])

    useEffect(() => {
      viewerInstanceRef.current = viewer ?? null
    }, [viewer])

    // Adjusts how native Pannellum listens for mouse events
    useEffect(() => {
      if (viewer) {
        viewer.syncTo(syncedTo)
      }
    }, [syncedTo, viewer])

    // Updates internal position when property value from parent changes.
    // Never lookAt lagging React props over a settled live/known pose (release
    // snap / cross-pane pending-sync thrash).
    useEffect(() => {
      if (isMouseDown.current || followRealtime) {
        return
      }
      const propsNorm = normalizeViewerPosition(viewerPosition)
      if (areViewerPositionsAlmostEqual(propsNorm, knownPosition.current)) {
        return
      }
      if (viewer) {
        const liveNorm = normalizeViewerPosition(
          removeOffset({
            yaw: viewer.getYaw(),
            pitch: viewer.getPitch(),
            hfov: viewer.getHfov(),
          }),
        )
        if (areViewerPositionsAlmostEqual(liveNorm, knownPosition.current)) {
          return
        }
      }
      updateKnownPosition(viewerPosition, false)
    }, [
      followRealtime,
      knownPosition,
      removeOffset,
      updateKnownPosition,
      viewer,
      viewerPosition,
    ])

    // When followRealtime, mirror parent position into the viewer (split-screen
    // Forge subordinate / locked prior overlay).
    useEffect(() => {
      if (!followRealtime || isMouseDown.current) {
        return
      }
      updateKnownPosition(viewerPosition, false, true)
    }, [followRealtime, updateKnownPosition, viewerPosition])

    // Intentional retarget (label aim, observation switch): apply parent pose even
    // when live≈known would make the normal prop-sync effect no-op.
    useEffect(() => {
      if (poseCommandKey == null || poseCommandKey === '') return
      if (appliedPoseCommandKeyRef.current === poseCommandKey) return
      appliedPoseCommandKeyRef.current = poseCommandKey
      updateKnownPosition(viewerPosition, false, true)
    }, [poseCommandKey, viewerPosition, updateKnownPosition])

    // Live drag: emit onUpdate while the user pans so overlays can track.
    useEffect(() => {
      if (!viewer) {
        return
      }

      const stopDragPoll = () => {
        if (dragRafRef.current != null) {
          cancelAnimationFrame(dragRafRef.current)
          dragRafRef.current = null
        }
      }

      const pollDuringDrag = () => {
        if (!isMouseDown.current || !mountedRef.current) {
          dragRafRef.current = null
          return
        }
        const withoutOffset = removeOffset({
          yaw: viewer.getYaw(),
          pitch: viewer.getPitch(),
          hfov: viewer.getHfov(),
        })
        knownPosition.current = normalizeViewerPosition(withoutOffset)
        onUpdateRef.current?.(knownPosition.current)
        dragRafRef.current = requestAnimationFrame(pollDuringDrag)
      }

      const handleMouseDown = () => {
        isMouseDown.current = true
        onDraggingRef.current?.(true)
        stopDragPoll()
        dragRafRef.current = requestAnimationFrame(pollDuringDrag)
      }

      const handleMouseUp = () => {
        if (!isMouseDown.current) {
          return
        }
        isMouseDown.current = false
        stopDragPoll()
        const withoutOffset = removeOffset({
          yaw: viewer.getYaw(),
          pitch: viewer.getPitch(),
          hfov: viewer.getHfov(),
        })
        // Commit live pose to React before clearing followRealtime. Bypass
        // handleUpdate's followRealtime gate (still true this tick).
        const committed = normalizeViewerPosition(withoutOffset)
        knownPosition.current = committed
        onUpdateRef.current?.(committed)
        onDraggingRef.current?.(false)
      }

      viewer.on('mousedown', handleMouseDown)
      viewer.on('mouseup', handleMouseUp)
      return () => {
        viewer.off('mousedown', handleMouseDown)
        viewer.off('mouseup', handleMouseUp)
        stopDragPoll()
      }
    }, [knownPosition, removeOffset, viewer])

    // Keeps track of native Pannellum animate finished events
    useEffect(() => {
      if (viewer) {
        const handleAnimateFinished = (data: ViewerPosition) => {
          if (!followRealtimeRef.current && !isMouseDown.current) {
            const withoutOffset = removeOffset(data)
            updateKnownPosition(withoutOffset, true, false)
          }
        }
        viewer.on('animatefinished', handleAnimateFinished)
        return () => {
          viewer.off('animatefinished', handleAnimateFinished)
        }
      }
    }, [viewer, updateKnownPosition, removeOffset])

    // Notify the parent component about errors if any
    useEffect(() => {
      if (!viewer) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper error type
      const handleError = (err: any) => {
        if (!mountedRef.current) return
        sceneLoadingRef.current = false
        if (pendingSceneRef.current) {
          // The failure belongs to a superseded panorama; load the newer one.
          pendingSceneRef.current = false
          sceneLoadingRef.current = true
          viewer.loadScene(HOME_SCENE_ID)
          return
        }
        onErrorRef.current?.(err)
      }
      viewer.on('error', handleError)
      return () => {
        viewer.off('error', handleError)
      }
    }, [viewer])

    // Adjusts the size of native Pannellum viewer when splitting screen
    useEffect(() => {
      viewer?.resize()
    }, [viewer, isSolo])

    useEffect(() => {
      if (!viewer) {
        return
      }

      const handleResize = () => viewer.resize()

      window.addEventListener('resize', handleResize)
      window.addEventListener('orientationchange', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('orientationchange', handleResize)
      }
    }, [viewer])

    // After scene loads, simulate a tiny zoom to trigger the same sync flow as user interaction
    useEffect(() => {
      if (viewer) {
        const handleLoad = () => {
          caughtUpViewerRef.current = viewer
          sceneLoadingRef.current = false
          if (pendingSceneRef.current) {
            // A newer panorama was requested while this one was loading; apply
            // it now and stay in the loading state for the parent.
            pendingSceneRef.current = false
            sceneLoadingRef.current = true
            viewer.loadScene(HOME_SCENE_ID)
            return
          }
          onLoadRef.current?.()
          if (!followRealtime) {
            const currentHfov = viewer.getHfov()
            const nudgeAmount = 0.01

            viewer.setHfov(currentHfov - nudgeAmount)

            requestAnimationFrame(() => {
              viewer.setHfov(currentHfov)
            })
          }
        }
        viewer.on('load', handleLoad)
        // Pannellum loads `Image` panoramas while constructing the viewer, so
        // the first scene can already be up before this listener attaches.
        if (viewer.isLoaded?.() && caughtUpViewerRef.current !== viewer) {
          handleLoad()
        }
        return () => {
          viewer.off('load', handleLoad)
        }
      }
    }, [viewer, followRealtime])

    const applyScene = (src: string, panorama: HTMLImageElement) => {
      if (!mountedRef.current) return
      // Seed known pose from parent before create/loadScene so cold opens and
      // observation swaps do not briefly build a 0/0 scene then lookAt later.
      const desired = normalizeViewerPosition(viewerPositionRef.current)
      if (!areViewerPositionsAlmostEqual(desired, knownPosition.current)) {
        knownPosition.current = desired
      }
      const withOffset = injectOffset(knownPosition.current)
      appliedSrcRef.current = src
      appliedOffsetRef.current = angleOffset
      const scene = {
        panorama,
        type: 'equirectangular',
        pitch: withOffset.pitch,
        yaw: withOffset.yaw,
        hfov: withOffset.hfov,
        ...(minHfov != null ? { minHfov } : {}),
        ...(maxHfov != null ? { maxHfov } : {}),
      }

      const existing = viewerInstanceRef.current
      if (!existing) {
        if (!ref.current) return
        sceneLoadingRef.current = true
        const created = createViewer(ref.current, scene, { minHfov, maxHfov })
        viewerInstanceRef.current = created
        if (created.isLoaded?.()) {
          sceneLoadingRef.current = false
        } else {
          // A preloaded HTMLImageElement initializes synchronously. If it did
          // not load, pannellum fired its error before React could subscribe.
          sceneLoadingRef.current = false
          queueMicrotask(() => {
            if (
              mountedRef.current &&
              viewerInstanceRef.current === created &&
              !created.isLoaded?.()
            ) {
              onErrorRef.current?.('Failed to initialize panorama viewer')
            }
          })
        }
        return
      }

      existing.addScene(HOME_SCENE_ID, scene)
      // Pannellum drops `loadScene` calls made mid-load; the load/error
      // handlers apply the swap once the current scene settles.
      if (sceneLoadingRef.current) {
        pendingSceneRef.current = true
        return
      }
      pendingSceneRef.current = false
      sceneLoadingRef.current = true
      existing.loadScene(HOME_SCENE_ID)
    }

    const applySceneRef = useRef(applyScene)
    applySceneRef.current = applyScene

    /**
     * Decodes the panorama, then either creates the viewer with it as the first
     * scene or swaps it into the live viewer. Nothing keeps a reference to the
     * decoded image: pannellum uploads it as a texture and drops it, and repeat
     * decodes of the same src come from the browser cache.
     */
    const preloadAndApply = (src: string) => {
      let cancelled = false

      const img = new Image()
      if (!src.startsWith('blob:')) {
        img.crossOrigin = 'anonymous'
      }
      img.onload = () => {
        if (cancelled || !mountedRef.current) return
        applySceneRef.current(src, img)
      }
      img.onerror = () => {
        // Do not silently loadScene with the same URL — that doubles work and
        // fires a late viewer error into the next observation's epoch.
        if (cancelled || !mountedRef.current) return
        onErrorRef.current?.(`Failed to preload panorama image: ${src}`)
      }
      img.src = src

      return () => {
        cancelled = true
        // Detach handlers so a late onload/onerror cannot loadScene on a
        // destroyed viewer (which can fire `error` into the next observation).
        img.onload = null
        img.onerror = null
      }
    }

    // Creates the viewer on the first panorama, then swaps scenes in place so
    // later images reuse the same WebGL context (no remount, no re-init).
    useEffect(() => {
      if (!image) return
      return preloadAndApply(image)
      // preloadAndApply reads the latest offset via refs at apply time.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
    }, [image])

    // Angle calibration can change without a new image (Site View offset
    // panel); re-orient the panorama that is already on screen.
    useEffect(() => {
      if (appliedSrcRef.current !== image) return
      if (appliedOffsetRef.current === angleOffset) return
      return preloadAndApply(image)
      // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
    }, [angleOffset, image])

    const divStyle = useMemo(
      () => ({
        width: '100%',
        height: '100%',
      }),
      [],
    )

    return <div style={divStyle} ref={ref} />
  },
)

Pannellum.displayName = 'Pannellum'
